import { type CSSProperties, memo, useCallback, useMemo } from 'react';
import isEqual from 'react-fast-compare';

import { type Clip } from '@air/api/types';
import { useBreakpointsContext } from '@air/provider-media-query';

import { type ItemProps } from '~/components/Gallery/types';
import { AssetTableRow, type AssetTableRowProps } from '~/components/TableView/AssetTableRow';
import { MobileTableRowContainer } from '~/components/TableView/MobileTableRow/MobileTableRowContainer';
import { PlaceholderAssetTableRow } from '~/components/TableView/PrivateTableView/PlaceholderAssetTableRow';
import { PrivateAssetNameCell } from '~/components/TableView/PrivateTableView/PrivateAssetNameCell';
import { PrivateDraggableAssetTableRow } from '~/components/TableView/PrivateTableView/PrivateDraggableAssetTableRow';
import { useRenamingContext } from '~/components/TableView/PrivateTableView/TableViewItemRenamingContext';
import { PrivateAssetCustomFieldTableCell } from '~/components/TableView/TableRow/CustomFieldTableCell/PrivateAssetCustomFieldTableCell';
import { DesktopTableRowContainer } from '~/components/TableView/TableRow/DesktopTableRowContainer';
import { type BasicTableRowProps } from '~/components/TableView/types';
import { useGetPrivateTableViewAssetMenuOptionsCallback } from '~/components/TableView/useGetPrivateTableViewAssetMenuOptionsCallback';
import { QueryParamNames } from '~/constants/search';
import { TABLE_ROW } from '~/constants/testIDs';
import { useGoToPrivateAssetPage } from '~/hooks/useGoToPrivateAssetPage';
import { useOpenAssetInNewTab } from '~/hooks/useOpenAssetInNewTab';
import { usePrivateSelectionMenuOptions } from '~/hooks/usePrivateSelectionMenuOptions';
import { useWorkspaceHorizontalPadding } from '~/hooks/useWorkspaceHorizontalPadding';
import { AssetModalPanel } from '~/providers/AssetModalPanelContextProvider';
import { type ConfigViewOption } from '~/store/configViews/types';
import { getAssetGalleryItemType } from '~/utils/AssetUtils';
import { isPlaceholderClip } from '~/utils/placeholderClip';

export type PrivateAssetTableRowProps = {
  /** When set, the row shows these columns instead of the shared config view. */
  configViewOptions?: ConfigViewOption[];
  /**
   * `react-virtualized` warns that `style` property should be included in the rendered cell for positioning
   * so we've separated it from the `tx` prop
   * */
  style: CSSProperties;
} & BasicTableRowProps<Clip>;

const PrivateAssetTableRowContent = memo(({ configViewOptions, data: asset, style }: PrivateAssetTableRowProps) => {
  const { getSelectionMenuOptions } = usePrivateSelectionMenuOptions();
  const { setIsRenaming, getKeyboardListenerProps } = useRenamingContext();
  const { getTableViewAssetMenuOptionsCallback } = useGetPrivateTableViewAssetMenuOptionsCallback({
    onRename: () => setIsRenaming(asset.id),
  });
  const { responsiveHorizontalPadding } = useWorkspaceHorizontalPadding();
  const { isAboveMediumScreen } = useBreakpointsContext();
  const itemType = getAssetGalleryItemType(asset);
  const { goToAssetPage } = useGoToPrivateAssetPage();
  const { openAssetInNewTab } = useOpenAssetInNewTab();
  const onItemClick = useCallback(
    () =>
      goToAssetPage({
        asset,
        trackLocation: 'card-click',
      }),
    [asset, goToAssetPage],
  );
  const onItemCmdClick = useCallback(
    () =>
      openAssetInNewTab({
        asset,
        trackLocation: 'card-click',
      }),
    [asset, openAssetInNewTab],
  );

  const onCommentsClick = useCallback(
    () =>
      goToAssetPage({
        asset,
        query: {
          [QueryParamNames.amTab]: AssetModalPanel.COMMENTS,
        },
        trackLocation: 'comments-click',
      }),
    [asset, goToAssetPage],
  );

  const onVersionsClick = useCallback(() => {
    goToAssetPage({
      asset,
      query: {
        [QueryParamNames.versions]: true,
      },
      trackLocation: 'versions-click',
    });
  }, [asset, goToAssetPage]);

  const getItemMenuOptions = getTableViewAssetMenuOptionsCallback({ item: asset });

  const memoizedAssetProps: ItemProps = useMemo(
    () => ({
      isSelectable: true,
      getSelectionMenuOptions,
      getItemMenuOptions,
      onCommentsClick,
      onItemClick,
      onItemCmdClick,
      onVersionsClick,
    }),
    [getItemMenuOptions, getSelectionMenuOptions, onCommentsClick, onItemClick, onItemCmdClick, onVersionsClick],
  );

  const renderNameCell: AssetTableRowProps<Clip>['renderNameCell'] = useCallback(
    ({ isSelected, isHovering }) => (
      <PrivateAssetNameCell isHovering={isHovering} isSelected={isSelected} clip={asset} {...memoizedAssetProps} />
    ),
    [asset, memoizedAssetProps],
  );

  const keyboardListenerProps = useMemo(() => getKeyboardListenerProps(asset.id), [asset.id, getKeyboardListenerProps]);

  return isAboveMediumScreen ? (
    <DesktopTableRowContainer
      style={{
        paddingLeft: responsiveHorizontalPadding,
        paddingRight: responsiveHorizontalPadding,
        ...style,
      }}
      // biome-ignore lint/plugin: TABLE_ROW test id includes the item type so existing table tests can tell assets from files
      data-testid={`${TABLE_ROW}-${itemType}`}
      data-title={asset.title || asset.importedName}
    >
      <PrivateDraggableAssetTableRow asset={asset}>
        <AssetTableRow
          CustomFieldTableCell={PrivateAssetCustomFieldTableCell}
          assetProps={memoizedAssetProps}
          configViewOptions={configViewOptions}
          data={asset}
          renderNameCell={renderNameCell}
          keyboardListenerProps={keyboardListenerProps}
        />
      </PrivateDraggableAssetTableRow>
    </DesktopTableRowContainer>
  ) : (
    <MobileTableRowContainer
      style={{
        paddingLeft: responsiveHorizontalPadding,
        paddingRight: responsiveHorizontalPadding,
        ...style,
      }}
    >
      {renderNameCell({ isSelected: false, isHovering: false })}
    </MobileTableRowContainer>
  );
}, isEqual);

PrivateAssetTableRowContent.displayName = 'PrivateAssetTableRowContent';

export const PrivateAssetTableRow = memo(({ configViewOptions, data: asset, style }: PrivateAssetTableRowProps) => {
  if (isPlaceholderClip(asset)) {
    return <PlaceholderAssetTableRow asset={asset} style={style} />;
  }

  return <PrivateAssetTableRowContent configViewOptions={configViewOptions} data={asset} style={style} />;
}, isEqual);

PrivateAssetTableRow.displayName = 'PrivateAssetTableRow';
